import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const ACTION_META = {
  CONFIRM_ASSEMBLED: { label: 'Confirm Assembled', icon: CheckCircle2, color: 'text-green-700', requiresReason: false },
  RECLAIM_TO_STORED: { label: 'Reclaim → STORED', icon: RotateCcw, color: 'text-blue-700', requiresReason: true },
  WRITE_OFF: { label: 'Write Off (LOST)', icon: Trash2, color: 'text-red-700', requiresReason: true },
};

const ComboVarianceReconciliation = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resolutions, setResolutions] = useState({}); // usn_code -> { action, reason }
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get(`/combo-reconciliation/variance/${sessionId}`);
      setData(res.data);
      // Pre-seed resolutions with suggested_action
      const seed = {};
      (res.data.components || []).forEach(c => {
        (c.variance_usns || []).forEach(v => {
          seed[v.usn_code] = {
            action: v.suggested_action,
            reason: '',
          };
        });
      });
      setResolutions(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load variance');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const setAction = (usn, action) => setResolutions(prev => ({ ...prev, [usn]: { ...(prev[usn] || {}), action } }));
  const setReason = (usn, reason) => setResolutions(prev => ({ ...prev, [usn]: { ...(prev[usn] || {}), reason } }));

  const submitAll = async () => {
    const items = Object.entries(resolutions).map(([usn, r]) => ({
      usn_code: usn,
      action: r.action,
      reason: r.reason || '',
    }));
    if (items.length === 0) {
      toast.error('No variance USNs to resolve');
      return;
    }
    for (const it of items) {
      const meta = ACTION_META[it.action];
      if (!meta) return toast.error(`Invalid action for ${it.usn_code}`);
      if (meta.requiresReason && !it.reason.trim()) {
        return toast.error(`Reason required for ${meta.label} on ${it.usn_code}`);
      }
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post(`/combo-reconciliation/variance/${sessionId}/resolve`, { resolutions: items });
      const d = res.data;
      toast.success(`Applied ${d.applied?.length || 0}, skipped ${d.skipped?.length || 0}. Session balanced: ${d.session_balanced ? 'YES' : 'NO'}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolution failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Variance Reconciliation</h1>
                <p className="text-sm text-[#E8DCC8] font-mono">{sessionId}</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={isLoading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {!data && <p className="text-center text-gray-500 py-8">Loading…</p>}

        {data && (
          <>
            <Card>
              <CardContent className="p-4 flex flex-wrap gap-3">
                <Badge variant="outline">Flow: <b className="ml-1">{data.flow}</b></Badge>
                <Badge variant="outline">Status: <b className="ml-1">{data.session_status}</b></Badge>
                <Badge variant="outline">Components: <b className="ml-1">{(data.components || []).length}</b></Badge>
              </CardContent>
            </Card>

            {(data.components || []).map(c => (
              <Card key={c.component_barcode} data-testid={`comp-card-${c.component_barcode}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-mono">{c.component_barcode}</CardTitle>
                      <p className="text-sm text-gray-600">{c.component_name} • {c.qty_per_combo} per combo</p>
                    </div>
                    <div className="flex gap-2 text-sm">
                      <Badge variant="outline">Demand: <b className="ml-1">{c.demand}</b></Badge>
                      <Badge variant="outline" className="text-green-700">Consumed: <b className="ml-1">{c.consumed_count}</b></Badge>
                      <Badge variant="outline" className="text-amber-700">On Floor: <b className="ml-1">{c.floor_count}</b></Badge>
                      <Badge variant="outline" className="text-red-700">Variance USNs: <b className="ml-1">{c.variance_count}</b></Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {c.variance_count === 0 ? (
                    <p className="text-sm text-gray-500 px-6 py-3">No variance — demand matches consumed.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>USN</TableHead>
                          <TableHead className="text-center">Currently CONSUMED</TableHead>
                          <TableHead className="text-center">On Floor</TableHead>
                          <TableHead>Suggested</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(c.variance_usns || []).map(v => {
                          const r = resolutions[v.usn_code] || { action: v.suggested_action, reason: '' };
                          const meta = ACTION_META[r.action] || ACTION_META.CONFIRM_ASSEMBLED;
                          return (
                            <TableRow key={v.usn_code} data-testid={`var-row-${v.usn_code}`}>
                              <TableCell className="font-mono text-xs">{v.usn_code}</TableCell>
                              <TableCell className="text-center">
                                {v.in_consumed ? <CheckCircle2 className="w-4 h-4 inline text-green-600" /> : '—'}
                              </TableCell>
                              <TableCell className="text-center">
                                {v.on_floor ? <CheckCircle2 className="w-4 h-4 inline text-amber-600" /> : '—'}
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">{v.note}</TableCell>
                              <TableCell>
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  value={r.action}
                                  onChange={(e) => setAction(v.usn_code, e.target.value)}
                                  data-testid={`action-${v.usn_code}`}
                                >
                                  {Object.keys(ACTION_META).map(k => (
                                    <option key={k} value={k}>{ACTION_META[k].label}</option>
                                  ))}
                                </select>
                              </TableCell>
                              <TableCell>
                                {meta.requiresReason ? (
                                  <Input
                                    placeholder="Reason (required)"
                                    value={r.reason}
                                    onChange={(e) => setReason(v.usn_code, e.target.value)}
                                    className="text-sm"
                                    data-testid={`reason-${v.usn_code}`}
                                  />
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-end">
              <Button
                onClick={submitAll}
                disabled={submitting || Object.keys(resolutions).length === 0}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="submit-resolutions-btn"
              >
                {submitting ? 'Applying…' : `Apply ${Object.keys(resolutions).length} resolution(s)`}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ComboVarianceReconciliation;
